/**
 * Tag Removed Trigger Component
 *
 * Trigger: "tag removed"
 * Shows: tag multi-select using client tags from session
 */

import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AddableAutocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { TextField } from 'src/components/TextField';
import { useSession } from 'src/contexts/SessionContext';

import { TriggerComponentProps } from '../automationTriggers';

type TagOption = { id: string; title: string };

export const TagRemovedTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { session } = useSession();
  const { setFieldValue } = useFormikContext();

  // Parse tags from comma-separated string
  const selectedTags = automation.tags
    ? automation.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  // Get client tags from session (handle null session)
  const clientTags = session?.clientTags ?? [];
  const options: TagOption[] = clientTags.map((tag) => ({
    id: tag,
    title: tag,
  }));

  // For display, also show tags that might not be in current options
  const allTagOptions = [...options];
  selectedTags.forEach((tag) => {
    if (!options.some((opt) => opt.id === tag)) {
      allTagOptions.push({ id: tag, title: tag });
    }
  });

  const selectedOptions = allTagOptions.filter((opt) =>
    selectedTags.includes(opt.id)
  );

  if (readOnly) {
    if (selectedOptions.length === 0) {
      return <span>{t('automations.triggers.tagRemoved')}</span>;
    }
    const tagNames = selectedOptions.map((opt) => opt.title).join(', ');
    return <span>{t('automations.triggers.tagRemovedNamed', { tags: tagNames })}</span>;
  }

  // Store as comma-separated string in tags field
  const handleChange = (
    _event: unknown,
    value: (TagOption | string)[] | null
  ) => {
    const tagValues = (value || []).map((opt) =>
      typeof opt === 'string' ? opt : opt.id
    );
    setFieldValue(`automations.${index}.tags`, tagValues.join(','));
  };

  // Edit mode
  return (
    <Box sx={{ maxWidth: 400 }}>
      <AddableAutocomplete<TagOption, true>
        value={selectedOptions}
        options={allTagOptions}
        multiple
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('automations.triggerLabels.tags')}
          />
        )}
      />
    </Box>
  );
};
